import { useCallback, useContext, useState } from "react";
import { axiosInstance } from "../../axios/axiosInstance";
import { useNavigate, useParams } from "react-router-dom";
import { useNoteAlert } from "../pleaseNote";
import { useAuth } from "../AuthContext";
import { GlobalContext } from "../GlobalContext";
import { ChangeOfAddressType } from "../../constants";

const parseData = (res) => (typeof res?.data === "string" ? JSON.parse(res.data) : res?.data);

// Till-powered Google Pay — fully self-contained (does NOT go through Shipping/PaymentProcessNew).
//
// The browser tokenizes Google Pay using the public Till config from REACT_APP_TILL_* env vars (see the
// component), then drives the WES order creation + Till debit through a single dedicated endpoint
// (TillGatewayService/ProcessOrder).
export function useTillGooglePayCheckout(netAmount, OrderId) {
  const [tillResult, setTillResult] = useState(null);
  const [charging, setCharging] = useState(false);
  const { showNoteAlert, showFlashScreen } = useNoteAlert();
  const { userId } = useAuth();
  const globalcontext = useContext(GlobalContext);
  const history = useNavigate();
  const { orderId } = useParams();

  const buildOrderParams = () => {
    const c = globalcontext;
    const params = {
      userId: String(userId),
      orderId: String(orderId ?? c.CartItem?.orderid ?? OrderId ?? ""),
      mobileNumber: c.smsNumber ?? "",
      amount: c.netAmount,
      shipNotes: c.orderCommands,
      shippingAddress: c.shippingAddress,
      shipcode: c.shippingCode,
      shipcost: c.shippingCost,
      orderCommands: c.orderCommands,
      transaction_token: "",
      addressType: "",
      signRequired: false,
      isSaveCard: false,
      uuid: "0",
      paymentMode: "GOOGLEPAY",
      cardType: "TILLGOOGLEPAY",
      grossAmount: String(c.CartItem?.totalcost ?? ""),
      totalTax: c.totalTax,
      deliveryMethod: c.deliveryMethod,
      shippingService: c.shippingMode || c.deliveryMethod,
      purchaseOrderNumber: c.poNumber,
      promoCode: c.promoCode,
    };

    if (c.IsInterNational === false) {
      if (c.addressType == ChangeOfAddressType.changeOfAddress || c.addressType == ChangeOfAddressType.dropShippment) {
        if (c.addressType == ChangeOfAddressType.changeOfAddress) params.addressType = "A";
        if (c.addressType == ChangeOfAddressType.dropShippment) params.addressType = "D";
        if (c.shippingAddress) {
          params.shippingAddress = c.shippingAddress;
          params.signRequired = c.shippingAddress.signatureRequired;
        }
      }
    }
    return params;
  };

  // One server call: create the WES order record + charge the Google Pay token through Till, then redirect.
  const processOrder = useCallback(
    (googlePayToken) => {
      if (!googlePayToken) {
        showNoteAlert("We couldn't read your Google Pay details. Please try again.");
        return false;
      }
      setCharging(true);
      showFlashScreen({
        messages: [
          "Processing your order, please wait…",
          "Confirming your Google Pay details with Till…",
          "Authorising your payment…",
          "Almost there, finalising your order…",
          "Please do not refresh or close this window.",
        ],
      });
      axiosInstance
        .post("TillGatewayService/ProcessOrder", { ...buildOrderParams(), googlePayToken })
        .then((res) => {
          const data = parseData(res);
          setTillResult(data);
          if (!data?.successful) {
            showFlashScreen(false);
            // Till returned a 3DS challenge — hand the shopper off to complete it.
            if (data?.redirectUrl) {
              window.location.href = data.redirectUrl;
              return false;
            }
            if (data?.status === "PAYMENT_PROCESS") {
              showNoteAlert("Payment is being processed for this order. Please wait for a moment and try again.");
            } else if (data?.status === "ORDER_STATUS") {
              showNoteAlert("Your order status has been changed. Please check and try again.");
            } else {
              showNoteAlert(
                "Your Google Pay payment could not be processed. Please try again or use a different payment method.",
              );
            }
            return false;
          }
          if (data?.redirectRef) {
            history("/billinfo/" + data.redirectRef, { replace: true });
          }
          showFlashScreen(false);
        })
        .catch(() => {
          showFlashScreen(false);
          showNoteAlert(
            "We couldn't reach Till to complete your purchase. Please check your connection and try again.",
          );
          return false;
        })
        .finally(() => {
          setCharging(false);
          showFlashScreen(false);
        });
    },
    // buildOrderParams reads the latest globalcontext via closure on each call.
    [userId, OrderId, globalcontext],
  );

  return { tillResult, charging, processOrder };
}
